import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogOut, Home, Calendar, Users, FileText, Settings, BarChart3, HelpCircle, Bell, ExternalLink, Send, Trash2, Search, Plus, Clock, Menu, X, AlertCircle, Building2 } from 'lucide-react';
import { apiGet, apiPost } from '../../utils/api';
import Availability from './Availability';
import Bookings from './Bookings';
import PatientDetail from './PatientDetail';
import TherapistHomePage from './TherapistHomePage';
import PracticeSettings from '../practice/PracticeSettings';
import DynamicHeader from '../common/DynamicHeader';
import GoogleIntegration from '../settings/GoogleIntegration';
import { Button, Card, Navigation, NavigationItem, UserHeader, CloseButton, Divider, Spinner } from '../design-system';
import NotificationPromptModal from '../notifications/NotificationPromptModal';
import NotificationManager from '../notifications/NotificationManager';


const TherapistDashboard = () => {
  const { user, logout, userInfo } = useAuth();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpSection, setHelpSection] = useState('');
  const [previousPage, setPreviousPage] = useState('dashboard');
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [hasNotificationSubscription, setHasNotificationSubscription] = useState(false);


  const handleLogout = () => {
    logout();
  };

  const handleHelpClick = (section) => {
    setHelpSection(section);
    setShowHelp(true);
  };

  const closeHelp = () => {
    setShowHelp(false);
    setHelpSection('');
  };


  // Generic navigation function for consistent patterns throughout the app
  const navigateToSection = (newSection) => {
    setPreviousPage(activeSection);  // Remember where we came from
    setActiveSection(newSection);     // Go to new section
    
    // Update URL based on section
    if (newSection === 'dashboard') {
      navigate('/dashboard/therapist');
    } else if (newSection === 'patient-detail') {
      // Don't change URL for patient detail, it's handled by patientId
      return;
    } else {
      navigate(`/dashboard/therapist/${newSection}`);
    }
  };

  // Handle patient selection for detail view
  const handlePatientSelect = (patient) => {
    navigate(`/dashboard/therapist/patient/${patient.patientId}`);
  };



  // Determine active section based on URL
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/patient/')) {
      setActiveSection('patient-detail');
    } else if (path.includes('/availability')) {
      setActiveSection('availability');
    } else if (path.includes('/bookings')) {
      setActiveSection('bookings');
    } else if (path.includes('/practice')) {
      setActiveSection('practice');
    } else if (path.includes('/chat')) {
      setActiveSection('chat');
    } else if (path.includes('/analytics')) {
      setActiveSection('analytics');
    } else if (path.includes('/settings')) {
      setActiveSection('settings');
    } else if (path.includes('/notifications')) {
      setActiveSection('notifications');
    } else if (path.includes('/help')) {
      setActiveSection('help');
    } else {
      setActiveSection('dashboard');
    }
    
  }, [location.pathname]);

  // Fetch patient data when patientId is in URL
  useEffect(() => {
    if (patientId) {
      fetchPatientData(patientId);
    }
  }, [patientId]);

  const fetchPatientData = async (patientId) => {
    try {
      const response = await apiGet('/.netlify/functions/get-patients', userInfo?.userId);
      const patients = response.patients || [];
      const patient = patients.find(p => p.patientId === patientId);
      if (patient) {
        setSelectedPatient(patient);
        setActiveSection('patient-detail');
      } else {
        // Patient not found, redirect to dashboard
        navigate('/dashboard/therapist');
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      navigate('/dashboard/therapist');
    }
  };



  // Check initial notification subscription status
  useEffect(() => {
    checkInitialNotificationStatus();
  }, []);

  const checkInitialNotificationStatus = async () => {
    try {
      // Check both browser permission AND user subscription
      const browserPermission = Notification.permission === "granted";
      
      if (browserPermission && 'serviceWorker' in navigator && 'PushManager' in window) {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        
        // Only set as enabled if BOTH browser permission AND subscription exist
        setHasNotificationSubscription(!!subscription);
      } else {
        // No browser permission = no notifications possible
        setHasNotificationSubscription(false);
      }
    } catch (error) {
      console.error('Error checking initial notification status:', error);
      setHasNotificationSubscription(false);
    }
  };

  // Navigation menu items
  const menuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: Home, active: true },
    { id: 'availability', name: 'Availability', icon: Calendar },
    { id: 'bookings', name: 'Bookings', icon: FileText },
    { id: 'practice', name: 'Practice', icon: Building2 },
    { id: 'analytics', name: 'Analytics', icon: BarChart3 },
    { id: 'settings', name: 'Settings', icon: Settings },
    { id: 'notifications', name: 'Notifications', icon: Bell },
  ];

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <TherapistHomePage 
          onNavigateToSection={navigateToSection}
          hasNotificationSubscription={hasNotificationSubscription}
        />;
      case 'availability':
        return <Availability />;
      case 'patient-detail':
        return selectedPatient ? (
          <PatientDetail 
            patient={selectedPatient} 
            onPatientUpdate={() => {/* Handle patient updates if needed */}}
          />
        ) : null;
      case 'bookings':
        return <Bookings />;
      case 'practice':
        return <PracticeSettings />;
      case 'analytics':
        return <AnalyticsContent />;
      case 'settings':
        return <SettingsContent />;
      case 'notifications':
        return <NotificationsContent />;
      case 'help':
        return <HelpContent />;

      default:
        return <TherapistHomePage 
          onNavigateToSection={navigateToSection}
          hasNotificationSubscription={hasNotificationSubscription}
        />;
    }
  };


  // Other section components
  const AnalyticsContent = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Analytics</h2>
      <p className="text-sm md:text-base text-gray-600">Coming soon: Practice analytics, patient insights, and performance metrics.</p>
    </div>
  );

  const SettingsContent = () => (
    <div className="space-y-6">
      <GoogleIntegration />
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Account Settings</h2>
        <p className="text-sm md:text-base text-gray-600">Coming soon: Profile settings, practice preferences, and account management.</p>
      </div>
    </div>
  );


  const NotificationsContent = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Push Notifications</h2>
        <NotificationManager 
          userId={userInfo?.userId} 
          onSubscriptionChange={(subscription) => {
            console.log('Notification subscription changed:', subscription);
            setHasNotificationSubscription(!!subscription);
          }}
        />
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Notification History</h2>
        <p className="text-sm md:text-base text-gray-600">Coming soon: Notification history and preferences.</p>
      </div>
    </div>
  );

  const HelpContent = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
      <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Help & Support</h2>
      <p className="text-sm md:text-base text-gray-600">Coming soon: Help documentation, FAQs, and support contact information.</p>
    </div>
  );

  // Show error if user is not a therapist
  if (userInfo?.role && userInfo.role !== 'therapist') {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            This dashboard is only for therapists. You are logged in as a {userInfo.role}.
          </p>
          <p className="text-sm text-gray-500">
            Please contact support if you believe this is an error.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen" style={{backgroundColor: 'var(--color-background)'}}>
      <div className="flex h-full">
        {/* Overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar Navigation */}
        <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-sm border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}>
          <div className="p-4 md:p-6 h-full flex flex-col">
            <div className="flex items-center justify-between">
              <UserHeader
                userName={(() => {
                  // Try to get name from userInfo (from database) first
                  if (userInfo?.name) return userInfo.name;
                  
                  // Fall back to Netlify metadata
                  const fullName = userInfo?.user_metadata?.full_name || userInfo?.user_metadata?.name;
                  if (fullName) return fullName;
                  
                  // If no name, format email nicely
                  const email = user?.email;
                  if (email) {
                    const emailName = email.split('@')[0];
                    return emailName.charAt(0).toUpperCase() + emailName.slice(1);
                  }
                  
                  return 'Therapist';
                })()}
                icon={<Users className="h-5 w-5 md:h-6 md:w-6" />}
              />
              <div className="flex items-center justify-center">
                <CloseButton
                  onClick={() => setSidebarOpen(false)}
                  size="medium"
                />
              </div>
            </div>

            {/* Section Divider */}
            <div className="border-t border-gray-200 mt-2 mb-4 md:mt-3 md:mb-6"></div>

            {/* Navigation Menu - Scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <Navigation>
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <NavigationItem
                      key={item.id}
                      active={activeSection === item.id}
                      onClick={() => {
                        navigateToSection(item.id);
                        setSidebarOpen(false);
                      }}
                      icon={<IconComponent className="h-4 w-4 md:h-5 md:w-5" />}
                    >
                      {item.name}
                    </NavigationItem>
                  );
                })}
              </Navigation>
            </div>

            {/* Logout Button - Fixed at bottom */}
            <div className="mt-4 md:mt-8 pt-4 md:pt-6 border-t border-gray-200 flex-shrink-0">
              <NavigationItem
                onClick={handleLogout}
                icon={<LogOut className="h-4 w-4 md:h-5 md:w-5" />}
              >
                Logout
              </NavigationItem>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col h-full">
          {/* Header */}
          <DynamicHeader
            activeSection={activeSection}
            onMenuClick={() => setSidebarOpen(true)}
            onBackClick={() => {
              if (activeSection === 'patient-detail') {
                navigate('/dashboard/therapist');
              } else {
                navigateToSection('dashboard');
              }
            }}
            onHelpClick={() => handleHelpClick(activeSection)}
            sectionNames={{
              availability: 'Availability',
              bookings: 'Bookings',
              'patient-detail': selectedPatient ? (selectedPatient.name || 'Unnamed Patient') : 'Patient Detail',
              practice: userInfo?.practice?.practiceName || 'Practice'
            }}
            appName="TheraEngage"
            showHelpIcon={activeSection !== 'dashboard'}
          />

          {/* Page Content */}
          <div className="flex-1 p-3 md:p-6 overflow-auto">
            {renderSectionContent()}
          </div>
        </div>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {helpSection === 'availability' ? 'Availability Help' :
                 helpSection === 'bookings' ? 'Bookings Help' : 'Help'}
              </h3>
              <button
                onClick={closeHelp}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              {helpSection === 'availability' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Manage your availability for patient appointments. You can:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>Set your available time slots</li>
                    <li>Block off unavailable times</li>
                    <li>Manage recurring availability</li>
                    <li>Update your schedule</li>
                  </ul>
                </div>
              )}
              
              {helpSection === 'bookings' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Manage patient appointments and bookings. You can:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                    <li>View upcoming appointments</li>
                    <li>Manage booking requests</li>
                    <li>Reschedule appointments</li>
                    <li>Track patient attendance</li>
                  </ul>
                </div>
              )}
              
              {!['availability', 'bookings'].includes(helpSection) && (
                <p className="text-sm text-gray-600">
                  Help content for this section is coming soon.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Notification Prompt Modal - Auto-shows in PWA mode */}
      <NotificationPromptModal
        userId={userInfo?.userId}
        onSubscriptionChange={(subscription) => {
          setHasNotificationSubscription(!!subscription);
        }}
      />
    </div>
  );
};

export default TherapistDashboard; 